/**
 * Directorio: lista TODOS los espacios (cursos y comisiones) donde el usuario participa,
 * y permite iniciar/continuar conversaciones 1-a-1 con sus miembros.
 *
 * Render:
 *  - Espacios desplegables (acordeón). Si un espacio no tiene otros miembros, muestra mensaje vacío.
 *  - Click en un miembro → abre chat 1-a-1 unificado (todos los mensajes con esa persona).
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Loader2, Send, ArrowLeft, MessageSquare, ChevronDown, ChevronRight,
  Users, GraduationCap
} from 'lucide-react';
import { formatBsAs } from '../utils/fechaUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MensajesP2P({ token, currentUserId, isAdmin }) {
  const [espacios, setEspacios] = useState([]);
  const [loadingEspacios, setLoadingEspacios] = useState(true);
  const [espaciosAbiertos, setEspaciosAbiertos] = useState({});
  const [seleccionado, setSeleccionado] = useState(null);
  const [mensajes, setMensajes] = useState([]);
  const [loadingMensajes, setLoadingMensajes] = useState(false);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const scrollRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchEspacios = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/mensajes-internos/p2p/contactos`, { headers });
      const data = await r.json();
      const lista = data.espacios || [];
      setEspacios(lista);
      // Abrir por defecto los espacios con mensajes no leídos
      setEspaciosAbiertos(prev => {
        const next = { ...prev };
        lista.forEach(e => {
          if (next[e.id] === undefined) {
            next[e.id] = (e.no_leidos_total || 0) > 0;
          }
        });
        return next;
      });
    } catch (e) {
      console.error('Error contactos p2p:', e);
    } finally {
      setLoadingEspacios(false);
    }
  }, [token]);

  const fetchConversacion = useCallback(async (otroId) => {
    setLoadingMensajes(true);
    try {
      const r = await fetch(`${API_URL}/api/mensajes-internos/p2p/conversacion/${otroId}`, { headers });
      const data = await r.json();
      setMensajes(data.mensajes || []);
    } catch (e) {
      console.error('Error conv:', e);
    } finally {
      setLoadingMensajes(false);
      setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }), 50);
    }
  }, [token]);

  useEffect(() => { fetchEspacios(); }, [fetchEspacios]);

  // Polling cuando hay conversación abierta
  useEffect(() => {
    if (!seleccionado) return;
    const id = setInterval(() => fetchConversacion(seleccionado.id), 15000);
    return () => clearInterval(id);
  }, [seleccionado, fetchConversacion]);

  // Polling de espacios cuando NO hay conv abierta
  useEffect(() => {
    if (seleccionado) return;
    const id = setInterval(fetchEspacios, 60000);
    return () => clearInterval(id);
  }, [seleccionado, fetchEspacios]);

  const toggleEspacio = (espacioId) => {
    setEspaciosAbiertos(prev => ({ ...prev, [espacioId]: !prev[espacioId] }));
  };

  const abrirConv = (miembro, espacio) => {
    setSeleccionado({ ...miembro, _espacio: espacio });
    fetchConversacion(miembro.id);
  };

  const cerrarConv = () => {
    setSeleccionado(null);
    setMensajes([]);
    fetchEspacios();
  };

  const enviar = async () => {
    const t = texto.trim();
    if (!t || !seleccionado || enviando) return;
    setEnviando(true);
    try {
      const r = await fetch(`${API_URL}/api/mensajes-internos/p2p/enviar`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          destinatario_id: seleccionado.id,
          contenido: t,
          space_id: seleccionado._espacio?.id || null,
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Error al enviar');
      }
      setTexto('');
      await fetchConversacion(seleccionado.id);
    } catch (e) {
      alert(e.message || 'No se pudo enviar el mensaje');
    } finally {
      setEnviando(false);
    }
  };

  // === Vista: chat abierto ===
  if (seleccionado) {
    return (
      <div className="flex flex-col h-[600px]" data-testid="mensajes-p2p-chat">
        <div className="flex items-center gap-2 border-b pb-3 mb-3">
          <Button variant="ghost" size="sm" onClick={cerrarConv} data-testid="cerrar-chat-p2p">
            <ArrowLeft className="w-4 h-4 mr-1" /> Volver
          </Button>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm">{`${seleccionado.nombre || ''} ${seleccionado.apellido || ''}`.trim() || seleccionado.email}</p>
            {seleccionado._espacio && (
              <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded-full mt-0.5 ${
                seleccionado._espacio.tipo === 'comision'
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-violet-100 text-violet-700'
              }`}>
                {seleccionado._espacio.nombre}
              </span>
            )}
          </div>
        </div>
        <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-2 pr-1">
          {loadingMensajes ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : mensajes.length === 0 ? (
            <p className="text-center text-sm text-gray-400 py-8">Iniciá la conversación 👋</p>
          ) : mensajes.map(m => {
            const mio = m.remitente_id === currentUserId;
            return (
              <div key={m.id} className={`flex ${mio ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${
                  mio ? 'bg-primary text-white' : 'bg-gray-100 text-gray-900'
                }`}>
                  <p className="whitespace-pre-wrap break-words">{m.contenido}</p>
                  <p className={`text-[10px] mt-1 ${mio ? 'text-white/70' : 'text-gray-500'}`}>
                    {formatBsAs(m.created_at)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
        <div className="border-t pt-3 flex gap-2">
          <Input
            value={texto}
            onChange={e => setTexto(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); } }}
            placeholder="Escribí tu mensaje…"
            disabled={enviando}
            data-testid="mensaje-p2p-input"
          />
          <Button onClick={enviar} disabled={enviando || !texto.trim()} data-testid="enviar-p2p-btn">
            {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    );
  }

  // === Vista: lista de espacios + miembros ===
  return (
    <div data-testid="mensajes-p2p-directorio">
      {loadingEspacios ? (
        <div className="flex justify-center py-12" data-testid="directorio-loading">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : espacios.length === 0 ? (
        <div className="text-center py-12">
          <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          {isAdmin ? (
            <>
              <p className="text-gray-500">Todavía no hay cursos ni comisiones creados en el Campus.</p>
              <p className="text-xs text-gray-400 mt-1">Creá un espacio desde el panel de administración del Campus para empezar a chatear con sus miembros.</p>
            </>
          ) : (
            <>
              <p className="text-gray-500">No estás inscripto/a en ningún espacio del Campus.</p>
              <p className="text-xs text-gray-400 mt-1">Cuando te inscribas a un curso o comisión, vas a poder chatear con tus compañeres acá.</p>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-2" data-testid="lista-espacios">
          {espacios.map(esp => {
            const abierto = espaciosAbiertos[esp.id];
            const colorTipo = esp.tipo === 'comision'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : 'bg-violet-50 border-violet-200 text-violet-800';
            return (
              <div key={esp.id} className="border rounded-md overflow-hidden" data-testid={`espacio-${esp.id}`}>
                <button
                  onClick={() => toggleEspacio(esp.id)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 ${colorTipo} hover:brightness-95 transition`}
                  data-testid={`toggle-espacio-${esp.id}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {abierto ? <ChevronDown className="w-4 h-4 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 flex-shrink-0" />}
                    {esp.tipo === 'comision'
                      ? <Users className="w-4 h-4 flex-shrink-0" />
                      : <GraduationCap className="w-4 h-4 flex-shrink-0" />}
                    <span className="font-medium text-sm truncate text-left">{esp.nombre}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                    <span className="text-xs opacity-70">{esp.miembros?.length || 0}</span>
                    {esp.no_leidos_total > 0 && (
                      <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                        {esp.no_leidos_total}
                      </span>
                    )}
                  </div>
                </button>
                {abierto && (
                  <div className="divide-y bg-white">
                    {(!esp.miembros || esp.miembros.length === 0) ? (
                      <p className="py-4 px-3 text-xs text-gray-400 text-center">
                        Todavía no hay otros participantes en este espacio.
                      </p>
                    ) : esp.miembros.map(m => (
                      <div
                        key={`${esp.id}-${m.id}`}
                        onClick={() => abrirConv(m, esp)}
                        className="py-2.5 px-3 cursor-pointer hover:bg-gray-50 transition-colors"
                        data-testid={`contacto-p2p-${m.id}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm">
                              {`${m.nombre || ''} ${m.apellido || ''}`.trim() || m.email || 'Usuario sin identificar'}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {m.ultimo_mensaje
                                ? `${m.ultimo_es_mio ? 'Vos: ' : ''}${m.ultimo_mensaje}`
                                : 'Tocá para iniciar la conversación'}
                            </p>
                          </div>
                          <div className="text-right ml-3 flex-shrink-0">
                            {m.ultimo_ts && (
                              <p className="text-[10px] text-gray-400">{formatBsAs(m.ultimo_ts)}</p>
                            )}
                            {m.no_leidos > 0 && (
                              <span className="inline-block mt-1 bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                                {m.no_leidos}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
